import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import type {
  DragEndEvent,
  DragStartEvent,
  PointerActivationConstraint,
} from '@dnd-kit/core';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import type { SortingStrategy } from '@dnd-kit/sortable';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import Stack from '@mui/material/Stack';
import type { SxProps, Theme } from '@mui/material/styles';
import { createPortal } from 'react-dom';

import {
  DEFAULT_ACTIVATION_CONSTRAINT,
  DEFAULT_OVERLAY_Z_INDEX,
  type ReorderEvent,
} from '@modules/_shared/components/sortable-list/types';

export type SortableItemRenderProps<T> = {
  item: T;
  index: number;
  isDragOverlay: boolean;
};

export type SortableListProps<T extends { id: string }> = {
  items: T[];
  onReorder: (event: ReorderEvent<T>) => void;
  renderItem: (props: SortableItemRenderProps<T>) => ReactNode;
  renderDragOverlay?: (item: T) => ReactNode;
  strategy?: SortingStrategy;
  containerSx?: SxProps<Theme>;
  gap?: number;
  activationConstraint?: PointerActivationConstraint;
  disabled?: boolean;
  overlayZIndex?: number;
};

export const reorderArray = <T,>(
  list: T[],
  startIndex: number,
  endIndex: number,
) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export const SortableList = <T extends { id: string }>(
  props: SortableListProps<T>,
) => {
  const {
    items,
    onReorder,
    renderItem,
    renderDragOverlay,
    strategy = verticalListSortingStrategy,
    containerSx,
    gap = 0,
    activationConstraint = DEFAULT_ACTIVATION_CONSTRAINT,
    disabled,
    overlayZIndex = DEFAULT_OVERLAY_Z_INDEX,
  } = props;

  const [activeItem, setActiveItem] = useState<T | null>(null);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const item = items.find(i => i.id === event.active.id);
      if (item) {
        setActiveItem(item);
      }
    },
    [items],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const movedItem = items[oldIndex];
          const newItems = reorderArray(items, oldIndex, newIndex);
          onReorder({
            items: newItems,
            movedItem,
            fromIndex: oldIndex,
            toIndex: newIndex,
          });
        }
      }

      setActiveItem(null);
    },
    [items, onReorder],
  );

  const handleDragCancel = useCallback(() => {
    setActiveItem(null);
  }, []);

  const mouseSensor = useSensor(MouseSensor, { activationConstraint });
  const touchSensor = useSensor(TouchSensor, { activationConstraint });
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });
  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

  const itemIds = useMemo(() => items.map(item => item.id), [items]);

  const overlayContent = useMemo(() => {
    if (!activeItem) {
      return null;
    }
    if (renderDragOverlay) {
      return renderDragOverlay(activeItem);
    }
    return renderItem({ item: activeItem, index: -1, isDragOverlay: true });
  }, [activeItem, renderDragOverlay, renderItem]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToParentElement]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={itemIds} strategy={strategy} disabled={disabled}>
        <Stack
          sx={[
            {
              gap,
            },
            ...(Array.isArray(containerSx) ? containerSx : [containerSx]),
          ]}
        >
          {items.map((item, index) =>
            renderItem({ item, index, isDragOverlay: false }),
          )}
        </Stack>
      </SortableContext>
      {createPortal(
        <DragOverlay style={{ zIndex: overlayZIndex }}>
          {overlayContent}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
};
